import {
    Banner,
    BannerDescription,
    BannerLinks,
    BannerText,
    BannerTextContainer,
    BannerTitle,
    Button,
    FlexBox,
    FluidImage,
    H2,
    Link,
    P,
    tokensRaw,
} from '@vp/swan';
import styled from 'styled-components';
import { FormattedHTMLMessage, SupportedLocale, __, __image } from '@99designs/i18n';
import { sendNavigationEvent, usePage } from '@99designs/tracking';
import { NewDesignProductPresenter } from '../../lib/presenter/productsPresenter';
import { getNavigationData, useCrossPlatformTracking } from '../../lib/tracking';
import { CloudinaryImage } from '../CloudinaryImage';

interface LogoBannerProps {
    locale: SupportedLocale;
}

const StyledBannerImage = styled(FluidImage)`
    border-radius: ${tokensRaw.SwanSemBorderRadiusContainer};
`;

const ImageContainer = styled(FlexBox)`
    flex-basis: 50%;
`;

const StyledBanner = styled(Banner)`
    @media (max-width: ${tokensRaw.SwanBaseBreakpointXsEnd}) {
        flex-direction: column-reverse;
    }
`;

export function LogoBanner({ locale }: LogoBannerProps) {
    const { pageData } = usePage();
    const proLogoDesign = NewDesignProductPresenter({
        mpvId: 'proLogoDesign',
        name: __('Pro Logo Design'),
        service: {
            __typename: 'ServiceProduct',
            mpvId: 'proLogoDesign',
            productKey: '',
            pimMerchantVersion: 0,
        },
        print: null,
    });

    const crossPlatformTracking = useCrossPlatformTracking(locale, 'Logo-Banner');
    const href = proLogoDesign.ctaUrl(locale, crossPlatformTracking);

    return (
        <StyledBanner
            bgc="standard"
            variant="card"
            data-testid="brand-banner"
            textBoxHorizontalAlign="right"
        >
            <BannerTextContainer>
                <BannerText marginLeft="0">
                    <BannerTitle as={H2}>
                        <FormattedHTMLMessage
                            message={__('Your dream logo,<br />designed by a pro? Yes, please!')}
                        />
                    </BannerTitle>
                    <BannerDescription>
                        <P>
                            {__(
                                'Complete your brief, choose your designer, share your feedback and bring your dream logo to life.'
                            )}
                        </P>
                    </BannerDescription>
                    <BannerLinks>
                        <Button
                            skin="secondary"
                            render={(props) => (
                                <Link
                                    to={href}
                                    className={props.className}
                                    onClick={() => {
                                        sendNavigationEvent(
                                            getNavigationData(proLogoDesign, {
                                                pageName: pageData.pageName,
                                                pageStage: pageData.pageStage,
                                                pageSection: pageData.pageSection,
                                                pagePath: '/design',
                                                pageZone: 'Brand Banner',
                                            })
                                        );
                                    }}
                                >
                                    {props.children}
                                </Link>
                            )}
                        >
                            {proLogoDesign.name}
                        </Button>
                    </BannerLinks>
                </BannerText>
            </BannerTextContainer>
            <ImageContainer flexDirection="column" justifyContent="center" alignItems="flex-start">
                <CloudinaryImage
                    render={(props) => (
                        <StyledBannerImage
                            src={props.src}
                            srcSet={props.srcSet}
                            alt=""
                            role="presentation"
                        />
                    )}
                    imagePath={__image('expert_services/HubPage/brand-banner-image')}
                    width={936}
                />
            </ImageContainer>
        </StyledBanner>
    );
}
